import React, { useState, useEffect } from 'react';
import { 
  getDashboardStats, 
  getDashboardChartData, 
  getTopProducts,
  getLowStockDetails,
  getSalesByCategory,
  type DashboardStats, 
  type ChartData,
  type TopProduct,
  type LowStockItem,
  type CategorySales
} from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [categorySales, setCategorySales] = useState<CategorySales[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState<number>(7);

  useEffect(() => {
    fetchDashboardData();
  }, [chartPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, chartDataResult, topProductsData, lowStockData, categorySalesData] = await Promise.all([
        getDashboardStats(),
        getDashboardChartData(chartPeriod),
        getTopProducts(5, 30),
        getLowStockDetails(),
        getSalesByCategory(30)
      ]);
      
      setStats(statsData);
      setChartData(chartDataResult);
      setTopProducts(topProductsData);
      setLowStockItems(lowStockData);
      setCategorySales(categorySalesData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  if (loading) {
    return <LoadingSpinner message="Chargement du tableau de bord..." />;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>📊 Tableau de Bord</h2>
        <button className="btn-secondary" onClick={fetchDashboardData}>🔄 Actualiser</button>
      </div>
      
      {stats && (
        <>
          {/* KPI Cards with Growth Indicators */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>💰</div>
              <div className="stat-content">
                <h3>Ventes du jour</h3>
                <div className="stat-value">{formatNumber(stats.today_sales)} CFA</div>
                <div className={`stat-trend ${stats.daily_growth >= 0 ? 'positive' : 'negative'}`}>
                  {stats.daily_growth >= 0 ? '📈' : '📉'} {Math.abs(stats.daily_growth).toFixed(1)}% vs hier
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>📅</div>
              <div className="stat-content">
                <h3>Ventes du mois</h3>
                <div className="stat-value">{formatNumber(stats.month_sales)} CFA</div>
                <div className={`stat-trend ${stats.monthly_growth >= 0 ? 'positive' : 'negative'}`}>
                  {stats.monthly_growth >= 0 ? '📈' : '📉'} {Math.abs(stats.monthly_growth).toFixed(1)}% vs mois dernier
                </div>
                <div className="stat-subtitle">{stats.month_sales_count} transactions</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>⚠️</div>
              <div className="stat-content">
                <h3>Alertes Stock</h3>
                <div className="stat-value">{stats.low_stock_alerts}</div>
                <div className="stat-subtitle">Produits en rupture proche</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>📦</div>
              <div className="stat-content">
                <h3>Commandes en attente</h3>
                <div className="stat-value">{stats.pending_orders}</div>
                <div className="stat-subtitle">{stats.total_products} produits actifs</div>
              </div>
            </div>
          </div>

          {/* Sales Chart with Period Selector */}
          <div className="chart-container">
            <div className="chart-header">
              <h3>📈 Évolution des ventes</h3>
              <div className="chart-controls">
                <button 
                  className={chartPeriod === 7 ? 'btn-active' : 'btn-ghost'}
                  onClick={() => setChartPeriod(7)}
                >
                  7 jours
                </button>
                <button 
                  className={chartPeriod === 14 ? 'btn-active' : 'btn-ghost'}
                  onClick={() => setChartPeriod(14)}
                >
                  14 jours
                </button>
                <button 
                  className={chartPeriod === 30 ? 'btn-active' : 'btn-ghost'}
                  onClick={() => setChartPeriod(30)}
                >
                  30 jours
                </button>
              </div>
            </div>
            {chartData.length > 0 ? (
              <div className="simple-chart" style={{ display: 'flex', alignItems: 'flex-end', height: '280px', gap: '8px', justifyContent: 'space-evenly', padding: '10px' }}>
                {chartData.map((data, index) => {
                  const maxAmount = Math.max(...chartData.map(d => d.amount), 1);
                  const height = (data.amount / maxAmount) * 220;
                  
                  return (
                    <div key={`${data.date}-${index}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '30px', maxWidth: '80px' }}>
                      <div 
                        className="chart-bar"
                        style={{ 
                          width: '100%', 
                          background: 'linear-gradient(180deg, #3b82f6, #2563eb)', 
                          height: `${height}px`,
                          borderRadius: '8px 8px 0 0',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          padding: '8px 4px',
                          minHeight: '30px',
                          position: 'relative'
                        }}
                        title={`${formatNumber(data.amount)} CFA`}
                      >
                        <span style={{ fontSize: 'clamp(10px, 1.8vw, 12px)', color: 'white', fontWeight: 'bold' }}>
                          {data.amount > 0 ? `${(data.amount / 1000).toFixed(0)}k` : '0'}
                        </span>
                      </div>
                      <div style={{ fontSize: 'clamp(10px, 1.8vw, 12px)', marginTop: '8px', color: '#6b7280', textAlign: 'center', fontWeight: '600' }}>
                        {new Date(data.date).toLocaleDateString('fr', { day: '2-digit', month: '2-digit' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="empty-message">Aucune donnée disponible</p>
            )}
          </div>

          {/* Two Column Layout for Top Products and Low Stock */}
          <div className="dashboard-grid">
            {/* Top Products */}
            <div className="dashboard-card">
              <h3>🏆 Top 5 Produits (30 jours)</h3>
              {topProducts.length > 0 ? (
                <div className="top-products-list">
                  {topProducts.map((product, index) => (
                    <div key={product.product_id} className="top-product-item">
                      <div className="product-rank">#{index + 1}</div>
                      <div className="product-info">
                        <div className="product-name">{product.name}</div>
                        <div className="product-category">{product.category}</div>
                      </div>
                      <div className="product-stats">
                        <div className="product-revenue">{formatNumber(product.total_revenue)} CFA</div>
                        <div className="product-quantity">{product.total_quantity} vendus</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message">Aucune vente enregistrée</p>
              )}
            </div>

            {/* Low Stock Alerts */}
            <div className="dashboard-card alert-card">
              <h3>⚠️ Alertes Stock Bas</h3>
              {lowStockItems.length > 0 ? (
                <div className="low-stock-list">
                  {lowStockItems.slice(0, 5).map((item) => (
                    <div key={item.product_id} className="low-stock-item">
                      <div className="stock-info">
                        <div className="stock-name">{item.name}</div>
                        <div className="stock-category">{item.category}</div>
                      </div>
                      <div className="stock-levels">
                        <div className="stock-current">
                          Stock: <strong>{item.current_quantity}</strong>
                        </div>
                        <div className="stock-min">
                          Min: {item.minimum_quantity}
                        </div>
                        <div className="stock-shortage urgent">
                          Manque: {item.shortage}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message success">✅ Tous les stocks sont suffisants</p>
              )}
            </div>
          </div>

          {/* Category Sales Breakdown */}
          <div className="dashboard-card">
            <h3>📊 Ventes par Catégorie (30 jours)</h3>
            {categorySales.length > 0 ? (
              <div className="category-sales-grid">
                {categorySales.map((cat) => {
                  const totalRevenue = categorySales.reduce((sum, c) => sum + c.total_amount, 0);
                  const percentage = (cat.total_amount / totalRevenue * 100).toFixed(1);
                  
                  return (
                    <div key={cat.category} className="category-card">
                      <div className="category-header">
                        <h4>{cat.category}</h4>
                        <span className="category-percentage">{percentage}%</span>
                      </div>
                      <div className="category-bar">
                        <div 
                          className="category-bar-fill"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="category-stats">
                        <div className="category-revenue">{formatNumber(cat.total_amount)} CFA</div>
                        <div className="category-quantity">{cat.total_quantity} unités · {cat.sales_count} ventes</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="empty-message">Aucune catégorie disponible</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;